import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getBlueprintOutputs, downloadBlueprintPdf } from "../api/apiClient";

const TABS = ["Complete Blueprint", "Executive Summary", "Diagrams", "Developer Package"];

const NODE_COLORS = {
  user: "#00695c",
  frontend: "#0d47a1",
  backend: "#0055FF",
  service: "#1565c0",
  data_store: "#6a1b9a",
  integration: "#ef6c00",
  step: "#0d47a1",
};

/* ------------------------------------------------------------------ */
/* SVG flow diagram — pure rendering of the server-derived nodes/edges */
/* ------------------------------------------------------------------ */

const NODE_W = 176;
const NODE_H = 48;
const H_GAP = 28;
const V_GAP = 64;

function layoutDiagram(diagram) {
  const levels = {};
  const incoming = {};
  diagram.nodes.forEach((n) => {
    incoming[n.id] = [];
  });
  diagram.edges.forEach((e) => {
    if (incoming[e.target]) incoming[e.target].push(e.source);
  });
  // longest-path level assignment (graphs are small and acyclic by construction)
  const level = (id, seen = new Set()) => {
    if (levels[id] !== undefined) return levels[id];
    if (seen.has(id)) return 0;
    seen.add(id);
    const preds = incoming[id] || [];
    const l = preds.length ? Math.max(...preds.map((p) => level(p, seen))) + 1 : 0;
    levels[id] = l;
    return l;
  };
  diagram.nodes.forEach((n) => level(n.id));

  const rows = [];
  diagram.nodes.forEach((n) => {
    const l = levels[n.id] || 0;
    rows[l] = rows[l] || [];
    rows[l].push(n);
  });

  const maxRow = Math.max(...rows.map((r) => (r ? r.length : 0)), 1);
  const width = Math.max(maxRow * (NODE_W + H_GAP) + H_GAP, NODE_W + 2 * H_GAP);
  const positions = {};
  rows.forEach((row, l) => {
    if (!row) return;
    const rowWidth = row.length * NODE_W + (row.length - 1) * H_GAP;
    const startX = (width - rowWidth) / 2;
    row.forEach((n, i) => {
      positions[n.id] = {
        x: startX + i * (NODE_W + H_GAP),
        y: 20 + l * (NODE_H + V_GAP),
      };
    });
  });
  const height = 20 + rows.length * (NODE_H + V_GAP);
  return { positions, width, height };
}

function wrapLabel(label, maxChars = 22) {
  const words = String(label).split(" ");
  const lines = [];
  let current = "";
  words.forEach((w) => {
    if ((current + " " + w).trim().length > maxChars && current) {
      lines.push(current);
      current = w;
    } else {
      current = (current + " " + w).trim();
    }
  });
  if (current) lines.push(current);
  if (lines.length > 2) {
    lines.length = 2;
    lines[1] = lines[1].slice(0, maxChars - 1) + "…";
  }
  return lines;
}

function FlowDiagramSvg({ diagram }) {
  const { positions, width, height } = useMemo(() => layoutDiagram(diagram), [diagram]);
  return (
    <Box sx={{ overflowX: "auto" }}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={diagram.title}
      >
        <defs>
          <marker
            id={`arrow-${diagram.id}`}
            markerWidth="8"
            markerHeight="8"
            refX="7"
            refY="4"
            orient="auto"
          >
            <path d="M0,0 L8,4 L0,8 z" fill="#64748b" />
          </marker>
        </defs>
        {diagram.edges.map((e, i) => {
          const s = positions[e.source];
          const t = positions[e.target];
          if (!s || !t) return null;
          const x1 = s.x + NODE_W / 2;
          const y1 = s.y + NODE_H;
          const x2 = t.x + NODE_W / 2;
          const y2 = t.y;
          return (
            <g key={`e-${i}`}>
              <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#64748b"
                strokeWidth="1.5"
                markerEnd={`url(#arrow-${diagram.id})`}
              />
              {e.label && (
                <text
                  x={(x1 + x2) / 2}
                  y={(y1 + y2) / 2 - 4}
                  fontSize="10"
                  fill="#64748b"
                  textAnchor="middle"
                >
                  {e.label}
                </text>
              )}
            </g>
          );
        })}
        {diagram.nodes.map((n) => {
          const p = positions[n.id];
          if (!p) return null;
          const lines = wrapLabel(n.label);
          return (
            <g key={n.id}>
              <rect
                x={p.x}
                y={p.y}
                width={NODE_W}
                height={NODE_H}
                rx="10"
                fill={NODE_COLORS[n.kind] || NODE_COLORS.step}
              />
              {lines.map((line, i) => (
                <text
                  key={i}
                  x={p.x + NODE_W / 2}
                  y={p.y + NODE_H / 2 + (i - (lines.length - 1) / 2) * 14 + 4}
                  fontSize="12"
                  fontWeight="600"
                  fill="#ffffff"
                  textAnchor="middle"
                >
                  {line}
                </text>
              ))}
            </g>
          );
        })}
      </svg>
    </Box>
  );
}

/* ------------------------------------------------------------- */
/* Document renderer — sections/blocks from the output generation */
/* ------------------------------------------------------------- */

function BlockView({ block }) {
  if (block.type === "paragraph") {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {block.text}
      </Typography>
    );
  }
  if (block.type === "list") {
    return (
      <Box sx={{ mb: 1.5 }}>
        {block.title && (
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            {block.title}
          </Typography>
        )}
        <Box component="ul" sx={{ m: 0, pl: 2, display: "grid", gap: 0.5 }}>
          {block.items.map((item, i) => (
            <Box component="li" key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  mt: 0.9,
                  bgcolor: "primary.main",
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {item}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }
  if (block.type === "table") {
    return (
      <Table size="small" sx={{ mb: 2 }}>
        <TableHead>
          <TableRow>
            {block.headers.map((h) => (
              <TableCell key={h} sx={{ fontWeight: 700 }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {block.rows.map((row, i) => (
            <TableRow key={i}>
              {row.map((cell, j) => (
                <TableCell key={j}>{cell}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }
  if (block.type === "status") {
    return (
      <Alert
        severity={block.value ? "success" : "warning"}
        icon={block.value ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
        sx={{ mb: 1, fontWeight: 700 }}
      >
        {block.label}
      </Alert>
    );
  }
  return null;
}

export function DocumentView({ document }) {
  return (
    <Stack spacing={2.5}>
      {document.sections.map((section, i) => (
        <Box key={section.id}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            {i + 1}. {section.title}
          </Typography>
          {section.blocks.map((block, j) => (
            <BlockView key={j} block={block} />
          ))}
        </Box>
      ))}
    </Stack>
  );
}

/* ------------------------- */
/* Developer package export  */
/* ------------------------- */

/**
 * What "Export JSON" writes: the approved blueprint plus everything a coding
 * tool needs to report back — the stable Requirement IDs and the Build
 * Snapshot instructions it follows when the build is complete.
 */
export function developerPackageExport(pkg) {
  if (!pkg) return null;
  return {
    meta: pkg.meta,
    blueprint: pkg.blueprint,
    requirements: pkg.requirements || [],
    build_snapshot_instructions: pkg.build_snapshot_instructions || null,
    normalized: pkg.normalized,
  };
}

/* ------------------------- */
/* Main component            */
/* ------------------------- */

export default function FinalBlueprintOutputs({
  mapText = "",
  blueprintId = null,
  blueprintVersion = null,
  sessionId = null,
  onExportOrCopy,
}) {
  const theme = useTheme();
  const [tab, setTab] = useState(TABS[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const mapJson = useMemo(() => {
    try {
      return JSON.parse(mapText);
    } catch {
      return null;
    }
  }, [mapText]);

  const fetchOutputs = async () => {
    if (!blueprintId && !mapJson) {
      setError("The approved blueprint could not be parsed.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await getBlueprintOutputs({ blueprintId, mapJson, sessionId });
      setData(result);
    } catch (err) {
      setData(null);
      setError(err.response?.data?.detail || "Failed to generate blueprint outputs: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOutputs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapText, blueprintId]);

  const meta = data?.outputs?.human_blueprint?.meta;
  const devPkg = data?.outputs?.developer_package;
  const devRequirements = devPkg?.requirements || [];

  const handleDownloadPdf = async (kind) => {
    setDownloading(kind);
    try {
      const blob = await downloadBlueprintPdf({ kind, blueprintId, mapJson });
      const base = (meta?.map_id || meta?.title || "blueprint").toString().replace(/[^a-zA-Z0-9-_]/g, "-");
      triggerDownload(blob, `${base}-v${meta?.blueprint_version || "1"}-${kind}.pdf`);
      if (onExportOrCopy) onExportOrCopy();
    } catch (err) {
      setError(err.response?.data?.detail || "PDF download failed: " + err.message);
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadDeveloperJson = () => {
    const pkg = data?.outputs?.developer_package;
    if (!pkg) return;
    const blob = new Blob([JSON.stringify(developerPackageExport(pkg), null, 2)], { type: "application/json" });
    const base = (meta?.map_id || "blueprint").toString().replace(/[^a-zA-Z0-9-_]/g, "-");
    triggerDownload(blob, `${base}-v${meta?.blueprint_version || "1"}-developer-package.json`);
    if (onExportOrCopy) onExportOrCopy();
  };

  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 1,
        boxShadow: "0 6px 20px rgba(0, 0, 0, 0.09)",
        bgcolor: "background.paper",
      }}
    >
      <Stack spacing={3}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Final Blueprint Outputs
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Four coordinated views of the same final approved blueprint.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {data && (
              <Chip
                size="small"
                color={data.ready_for_coding ? "success" : "warning"}
                icon={data.ready_for_coding ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
                label={data.ready_for_coding ? "READY FOR CODING" : "NOT READY FOR CODING"}
              />
            )}
            {data && (
              <Chip size="small" variant="outlined" label={`Version ${data.blueprint_version}`} />
            )}
            {data?.consistency && (
              <Chip
                size="small"
                variant="outlined"
                color={data.consistency.status === "pass" ? "success" : "error"}
                label={
                  data.consistency.status === "pass"
                    ? "Outputs consistent"
                    : "Consistency check failed"
                }
              />
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            borderRadius: 2,
            bgcolor: theme.palette.action.selected,
            p: 1,
          }}
        >
          {TABS.map((t) => (
            <Button
              key={t}
              onClick={() => setTab(t)}
              size="small"
              variant={tab === t ? "contained" : "text"}
              color={tab === t ? "primary" : "inherit"}
              sx={{ textTransform: "none", borderRadius: 2, px: 2, py: 1 }}
            >
              {t}
            </Button>
          ))}
        </Box>

        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Generating outputs from the approved blueprint…
            </Typography>
          </Box>
        )}

        {error && !loading && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={fetchOutputs}>
                Retry
              </Button>
            }
          >
            {String(error)}
          </Alert>
        )}

        {data && !loading && tab === "Complete Blueprint" && (
          <Stack spacing={2}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon fontSize="small" />}
                disabled={downloading === "human-blueprint"}
                onClick={() => handleDownloadPdf("human-blueprint")}
                sx={{ textTransform: "none" }}
              >
                {downloading === "human-blueprint" ? "Preparing PDF…" : "Download PDF"}
              </Button>
            </Box>
            <DocumentView document={data.outputs.human_blueprint} />
          </Stack>
        )}

        {data && !loading && tab === "Executive Summary" && (
          <Stack spacing={2}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon fontSize="small" />}
                disabled={downloading === "executive-summary"}
                onClick={() => handleDownloadPdf("executive-summary")}
                sx={{ textTransform: "none" }}
              >
                {downloading === "executive-summary" ? "Preparing PDF…" : "Download PDF"}
              </Button>
            </Box>
            <DocumentView document={data.outputs.executive_summary} />
          </Stack>
        )}

        {data && !loading && tab === "Diagrams" && (
          <Stack spacing={3}>
            {data.outputs.diagrams.diagrams.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                The approved blueprint does not define enough structure to derive diagrams.
              </Typography>
            )}
            {data.outputs.diagrams.diagrams.map((diagram) => (
              <Paper
                key={diagram.id}
                elevation={0}
                sx={{ p: 2.5, border: 1, borderColor: "divider", borderRadius: 3 }}
              >
                <Typography variant="subtitle1" fontWeight={700}>
                  {diagram.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {diagram.description}
                </Typography>
                <FlowDiagramSvg diagram={diagram} />
              </Paper>
            ))}
          </Stack>
        )}

        {data && !loading && tab === "Developer Package" && (
          <Stack spacing={2}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
                The machine-readable Ready-for-Coding package: the approved blueprint with its verdict,
                a stable ID for every requirement, and the Build Snapshot instructions your coding tool
                follows when the build is complete.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon fontSize="small" />}
                onClick={handleDownloadDeveloperJson}
                sx={{ textTransform: "none" }}
              >
                Export JSON
              </Button>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {devPkg?.meta?.project_id && (
                <Chip size="small" variant="outlined" label={`Project ID ${devPkg.meta.project_id}`} />
              )}
              {devPkg?.meta?.blueprint_id && (
                <Chip size="small" variant="outlined" label={`Blueprint ID ${devPkg.meta.blueprint_id}`} />
              )}
              <Chip
                size="small"
                variant="outlined"
                label={`Version ${devPkg?.meta?.blueprint_version || data.blueprint_version}`}
              />
              {devRequirements.length > 0 && (
                <Chip size="small" color="primary" variant="outlined" label={`${devRequirements.length} requirement IDs`} />
              )}
            </Box>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2,
                borderRadius: 2,
                bgcolor: theme.palette.action.hover,
                fontFamily: "ui-monospace, monospace",
                fontSize: 13,
                overflowX: "auto",
                maxHeight: 480,
              }}
            >
              {JSON.stringify(data.outputs.developer_package.blueprint, null, 2)}
            </Box>
            {devRequirements.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                  Requirement register
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Your coding tool reports evidence against these IDs in the Build Snapshot
                  ({devPkg?.build_snapshot_instructions?.filename || "onepass-build-snapshot.json"}).
                  The instructions and a pre-filled template are included in the export.
                </Typography>
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Requirement ID</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Requirement</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {devRequirements.map((r) => (
                        <TableRow key={r.requirement_id}>
                          <TableCell sx={{ fontFamily: "ui-monospace, monospace", whiteSpace: "nowrap" }}>
                            {r.requirement_id}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{r.category_name || r.category}</TableCell>
                          <TableCell>{r.text}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
